import React, { useState } from 'react';
import { toast } from '../../toasts';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaFileExcel } from 'react-icons/fa';
import ContactService from '../../services/contact.service';
import { getError } from '../../utils';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { Section, DataTable, RowActionsMenu, Button, ExportModal, buildXlsxExport } from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';
import { useClientSort } from '../../hooks/useClientSort';

function AccountContacts({ accountId, rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const location = useLocation();
	const [deletingContact, setDeletingContact] = useState(null);
	const [exportOpen, setExportOpen] = useState(false);

	const { sortedRows, sortColumn, direction, onSort } = useClientSort(rows, {
		defaultColumn: 'name',
		accessors: { name: (contact) => contact.name },
	});

	const returnTo = () => encodeURIComponent(location.pathname + location.search);

	const handleCreateContact = () => {
		navigate(`/AdminScreen/accounts/account/${accountId}/newContact?returnTo=${returnTo()}`);
	};

	const handleEditContact = (contact) => {
		navigate(`/AdminScreen/contacts/editContact/${contact._id}?returnTo=${returnTo()}`);
	};

	async function handleConfirmDelete() {
		try {
			await ContactService.delete(deletingContact._id);
			toast.success('Contacto Eliminado');
			onRefetch();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setDeletingContact(null);
		}
	}

	const columns = [
		{ key: 'name', header: 'Nombre', sortable: true, render: (contact) => contact.name },
		{ key: 'email', header: 'Email', render: (contact) => contact.email },
		{ key: 'address', header: 'Dirección', render: (contact) => contact.address },
		{ key: 'phone', header: 'Teléfono', render: (contact) => contact.phone },
		{ key: 'folder', header: 'Carpeta', render: (contact) => contact.folder },
	];

	const exportFields = [
		{ key: 'name', label: 'Nombre', locked: true },
		{ key: 'email', label: 'Email' },
		{ key: 'address', label: 'Dirección' },
		{ key: 'phone', label: 'Teléfono' },
		{ key: 'folder', label: 'Carpeta' },
	];

	const exportGetters = {
		name: (contact) => contact.name || '',
		email: (contact) => contact.email || '',
		address: (contact) => contact.address || '',
		phone: (contact) => contact.phone || '',
		folder: (contact) => contact.folder || '',
	};

	const handleExport = (selectedKeys) =>
		buildXlsxExport({
			rows: sortedRows,
			fields: exportFields,
			selectedKeys,
			getters: exportGetters,
			sheetName: 'Contactos',
			filePrefix: 'contactos',
		});

	const renderRowActions = (contact) => (
		<RowActionsMenu
			items={[
				{ label: 'Editar', onClick: () => handleEditContact(contact) },
				{ label: 'Eliminar', onClick: () => setDeletingContact(contact), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<div className='tw:flex tw:justify-end tw:gap-2'>
				<Button size='sm' icon={<FaFileExcel />} onClick={() => setExportOpen(true)} disabled={rows.length === 0}>
					{t('action.export')}
				</Button>
				<Button size='sm' variant='primary' onClick={handleCreateContact}>
					Nuevo contacto
				</Button>
			</div>
			<Section title='Contactos'>
				<DataTable
					columns={columns}
					rows={sortedRows}
					loading={loading}
					error={error}
					sortColumn={sortColumn}
					direction={direction}
					onSort={onSort}
					rowActions={renderRowActions}
					emptyMessage='No se encontraron contactos para esta cuenta.'
				/>
			</Section>

			<ExportModal
				show={exportOpen}
				onClose={() => setExportOpen(false)}
				count={rows.length}
				fields={exportFields}
				subtitle='Contactos de esta cuenta'
				onExport={handleExport}
			/>

			<ConfirmationModal
				show={Boolean(deletingContact)}
				handleClose={() => setDeletingContact(null)}
				handleConfirm={handleConfirmDelete}
				title={t('title.deleteContact')}
				description={t('msg.confirmDeleteContact')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default AccountContacts;
